/**
 * FollowUpGroupedList Component - Displays follow-ups grouped by time periods
 */

import React, { useState } from "react";
import { Box, Typography, Collapse, IconButton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import ActionFollowUpCard from "./ActionFollowUpCard.jsx";

/**
 * Empty state component
 */
const EmptyState = ({ mode }) => {
  if (mode === "needsAction") {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
          ✅ All Share Load follow-ups are under control
        </Typography>
        <Typography variant="body2" color="text.secondary">
          New leads will appear here automatically.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: "center", py: 8 }}>
      <Typography variant="body2" color="text.secondary">
        No follow-ups found.
      </Typography>
    </Box>
  );
};

/**
 * FollowUpGroupedList Component
 */
const FollowUpGroupedList = ({
  grouped,
  mode,
  onAction,
  selectMode = false,
  selectedIds = [],
  onToggleSelect,
  busyById = {},
}) => {
  const [expanded, setExpanded] = useState({
    today: true,
    tomorrow: true,
    thisWeek: false, // Collapsed by default
    later: false,
  });

  const groups = [
    {
      key: "today",
      label: "🔴 TODAY",
      items: grouped.today || [],
    },
    {
      key: "tomorrow",
      label: "🟠 TOMORROW",
      items: grouped.tomorrow || [],
    },
    {
      key: "thisWeek",
      label: "⚪ THIS WEEK",
      items: grouped.thisWeek || [],
    },
    {
      key: "later",
      label: "⚪ LATER",
      items: grouped.later || [],
    },
  ];

  const toggleGroup = (groupKey) => {
    setExpanded((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // Check if all groups are empty
  const allEmpty = groups.every((group) => group.items.length === 0);

  if (allEmpty) {
    return <EmptyState mode={mode} />;
  }

  return (
    <Box>
      {groups.map((group) => {
        if (group.items.length === 0) return null;

        const isExpanded = expanded[group.key];

        return (
          <Box key={group.key} sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => toggleGroup(group.key)}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {group.label} ({group.items.length})
              </Typography>
              <IconButton size="small" onClick={(e) => {
                e.stopPropagation();
                toggleGroup(group.key);
              }}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={isExpanded}>
              {group.items.map((followUp) => {
                const id = followUp?.id || followUp?._id;
                return (
                  <ActionFollowUpCard
                    key={id}
                    followUp={followUp}
                    mode={mode}
                    onAction={onAction}
                    selectMode={selectMode}
                    selected={selectedIds.includes(id)}
                    onToggleSelect={() => onToggleSelect?.(id)}
                    actionLoading={Boolean(busyById[id])}
                  />
                );
              })}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default FollowUpGroupedList;

